<template>
    <div ref="avatarWrapper" class="vue-avatar--wrapper relative" :style="[style, customStyle]" aria-hidden="true">
        <!-- White background -->
        <div class="absolute inset-0 -z-10 h-full w-full rounded-full bg-white" />

        <!-- Loading state - show nothing while loading -->
        <div v-if="hasImageSrc && !imageLoaded && !allUrlsFailed" class="absolute inset-0 z-10 h-full w-full" :style="[style]" />

        <!-- Image layer - only show when image is loaded -->
        <div
            v-if="isImage"
            class="absolute inset-0 z-10 h-full w-full transition-opacity duration-300"
            :style="[imageStyle, customStyle]"
        />

        <!-- Letter fallback - only show when no image src or all images failed -->
        <div
            v-if="!hasImageSrc || allUrlsFailed"
            class="absolute inset-0 z-10 h-full w-full transition-opacity duration-300"
            :style="[fallbackStyle, customStyle]"
        >
            <span class="flex h-full w-full items-center justify-center">{{ userInitial }}</span>
        </div>

        <!-- Hidden img for error detection -->
        <img
            v-if="hasImageSrc && isIntersecting"
            class="vue-avatar--image"
            style="display: none"
            :src="currentSrc"
            @error="onImgError"
            @load="onImgLoad"
        />
    </div>
</template>

<script>
import { MEDIA_CDN_BASE } from '@/utils/media'
import _ from 'lodash'

const getInitials = (username) => {
    if (!username) return 'X'
    const parts = username.split(/[ -]/)
    let initials = ''
    for (let i = 0; i < _.size(parts); i += 1) {
        initials += parts[i].charAt(0)
    }
    if (_.size(initials) > 3 && initials.search(/[A-Z]/) !== -1) {
        initials = initials.replace(/[a-z]+/g, '')
    }
    initials = initials.substr(0, 3).toUpperCase()
    return initials
}

export default {
    name: 'Avatar',
    props: {
        username: {
            type: String,
        },
        initials: {
            type: String,
        },
        backgroundColor: {
            type: String,
        },
        color: {
            type: String,
        },
        customStyle: {
            type: Object,
        },
        inline: {
            type: Boolean,
        },
        size: {
            type: Number,
            default: 50,
        },
        rounded: {
            type: Boolean,
            default: true,
        },
        lighten: {
            type: Number,
            default: 80,
        },
        parser: {
            type: Function,
            default: getInitials,
            validator: (parser) => typeof parser('John', getInitials) === 'string',
        },
        border: {
            type: Object,
        },
        srcs: {
            type: Array,
            default: () => [],
        },
        platformId: {
            type: String,
            default: null,
        },
        platformUserId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            platformToAvatarSize: {
                twitch: 'small',
                youtube: 'small',
                tiktok: 'large',
                twitter: 'small',
                instagram: 'small',
            },
            backgroundColors: [
                '#F44336',
                '#FF4081',
                '#9C27B0',
                '#673AB7',
                '#3F51B5',
                '#2196F3',
                '#03A9F4',
                '#00BCD4',
                '#009688',
                '#4CAF50',
                '#8BC34A',
                '#CDDC39',
                /* '#FFEB3B' , */ '#FFC107',
                '#FF9800',
                '#FF5722',
                '#795548',
                '#9E9E9E',
                '#607D8B',
            ],

            currentUrlIndex: 0,
            retryCount: 0,
            maxRetries: 3, // Allows 3 retries for CDN URL
            imgError: false,
            lastErrorEvent: null,
            isIntersecting: false, // Controls if the image is in the viewport
            imageLoaded: false, // Track if image has successfully loaded
            imageLoadStarted: false, // Track if image loading has started
            retryDelay: 100, // Start with 100ms delay
            allUrlsFailed: false, // Track if we've exhausted all URLs
        }
    },
    computed: {
        background() {
            if (!this.isImage && this.username) {
                return this.backgroundColor || this.randomBackgroundColor(_.size(this.username), this.backgroundColors)
            }
            return undefined
        },
        imageStyle() {
            return {
                display: this.inline ? 'inline-flex' : 'flex',
                width: `${this.size}px`,
                height: `${this.size}px`,
                borderRadius: this.rounded ? '50%' : 0,
                background: `transparent url('${this.currentSrc}') no-repeat scroll center / ${this.size}px ${this.size}px content-box border-box`,
                userSelect: 'none',
            }
        },
        fallbackStyle() {
            return {
                display: this.inline ? 'inline-flex' : 'flex',
                width: `${this.size}px`,
                height: `${this.size}px`,
                borderRadius: this.rounded ? '50%' : 0,
                backgroundColor: this.background,
                color: this.color,
                lineHeight: `${this.size + Math.floor(this.size / 20)}px`,
                fontWeight: 'bold',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                userSelect: 'none',
                font: `${Math.floor(this.size / 2.5)}px/${this.size}px Helvetica, Arial, sans-serif`,
            }
        },
        style() {
            const style = {
                display: this.inline ? 'inline-flex' : 'flex',
                width: `${this.size}px`,
                height: `${this.size}px`,
                borderRadius: this.rounded ? '50%' : 0,
                lineHeight: `${this.size + Math.floor(this.size / 20)}px`,
                fontWeight: 'bold',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                userSelect: 'none',
                font: `${Math.floor(this.size / 2.5)}px/${this.size}px Helvetica, Arial, sans-serif`,
            }
            return style
        },
        userInitial() {
            if (!this.isImage) {
                const initials = this.initials || this.parser(this.username, getInitials)
                return initials
            }
            return ''
        },
        currentSrc() {
            // If platformId and platformUserId are provided, use CDN URL
            if (this.platformId && this.platformUserId) {
                return this.getDefaultAvatarUrl()
            }

            // Otherwise use the srcs array
            const src = this.srcs[this.currentUrlIndex]
            // Validate the URL to prevent undefined/null in URLs
            if (!src || src.includes('undefined') || src.includes('null')) {
                console.warn('Invalid avatar URL detected:', src)
                return null
            }
            return src
        },
        isImage() {
            return this.imageLoaded && !this.imgError && Boolean(this.currentSrc)
        },
        hasImageSrc() {
            return Boolean(this.currentSrc)
        },
    },
    watch: {
        // Reset image error when src changes
        src(newValue, oldValue) {
            if (newValue !== oldValue) this.imgError = false
        },
        srcs: {
            immediate: false,
            handler() {
                this.resetLoadingState()
                if (this.srcs.length > 0) {
                    this.loadImage()
                }
            },
        },
        // Watch platformId and platformUserId for changes
        platformId(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.resetLoadingState()
                if (this.platformId && this.platformUserId) {
                    this.loadImage()
                }
            }
        },
        platformUserId(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.resetLoadingState()
                if (this.platformId && this.platformUserId) {
                    this.loadImage()
                }
            }
        },
    },
    mounted() {
        if (!this.isImage) {
            this.$emit('avatar-initials', this.username, this.userInitial)
        }

        this.createObserver()
    },
    beforeUnmount() {
        if (this.observer) {
            this.observer.disconnect()
        }
    },
    methods: {
        initial: getInitials,

        getDefaultAvatarUrl() {
            return `${MEDIA_CDN_BASE}/${this.platformId}/avatars/${
                this.platformToAvatarSize[this.platformId]
            }/${this.platformUserId}`
        },

        createObserver() {
            const options = {
                root: null, // relative to the viewport
                threshold: 0.1, // 10% of the item must be visible
            }

            this.observer = new IntersectionObserver((entries, observer) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.isIntersecting = true
                        this.loadImage()
                        observer.unobserve(this.$refs.avatarWrapper)
                    }
                })
            }, options)

            this.observer.observe(this.$refs.avatarWrapper)
        },

        randomBackgroundColor(seed, colors) {
            return colors[seed % _.size(colors)]
        },

        onImgError(event) {

            // If using platformId/platformUserId (single CDN URL), retry up to maxRetries
            if (this.platformId && this.platformUserId) {
                if (this.retryCount < this.maxRetries) {
                    this.retryCount += 1
                    const delay = this.retryDelay * Math.pow(2, this.retryCount - 1) // Exponential backoff

                    setTimeout(() => {
                        this.loadImage()
                    }, delay)
                } else {
                    this.imgError = true
                    this.imageLoaded = false
                    this.allUrlsFailed = true
                }
                return
            }

            // For srcs array mode, handle CORS vs CDN differently
            const isCorsError = this.currentSrc && (
                this.currentSrc.includes('cdninstagram.com') ||
                this.currentSrc.includes('scontent-') ||
                !this.currentSrc.includes(MEDIA_CDN_BASE)
            )

            if (isCorsError) {
                this.retryCount = 0
                this.moveToNextUrl()
            } else {
                this.retryCount = 0
                this.moveToNextUrl()
            }
        },

        moveToNextUrl() {
            if (this.currentUrlIndex < this.srcs.length - 1) {
                this.currentUrlIndex += 1
                this.loadImage()
            } else {
                this.imgError = true
                this.imageLoaded = false
                this.allUrlsFailed = true // Prevent further attempts
            }
        },

        onImgLoad() {
            this.imageLoaded = true
            this.imgError = false
        },

        resetLoadingState() {
            this.imageLoaded = false
            this.imageLoadStarted = false
            this.imgError = false
            this.currentUrlIndex = 0
            this.retryCount = 0
            this.retryDelay = 100
            this.allUrlsFailed = false
        },

        loadImage() {
            if (!this.currentSrc || this.allUrlsFailed) {
                // If current URL is invalid, try next one
                if (!this.currentSrc && this.currentUrlIndex < this.srcs.length - 1) {
                    this.currentUrlIndex += 1
                    this.loadImage()
                }
                return
            }

            this.imageLoadStarted = true
            const img = new Image()
            img.onload = this.onImgLoad
            img.onerror = this.onImgError
            img.src = this.currentSrc
        },
    },
}
</script>
